<template>
  <v-container>
    <v-row v-if="activeOutage">
      <v-col cols="12">
        <v-alert
          dismissible
          type="warning"
          border="left"
          elevation="2"
          colored-border
          icon="mdi-alert-octagon"
        >
          <div class="title font-weight-bold mb-1">Važno obaveštenje</div>
          <div>{{ activeOutage.message }}</div>
          <div class="caption text--secondary mt-2">
            Objavljeno: {{ formatDate(activeOutage.created_at) }}
          </div>
        </v-alert>
      </v-col>
    </v-row>

    <v-divider class="my-6" v-if="isAdminOrRestaurant && activeOutage"></v-divider>

    <v-row v-if="isAdminOrRestaurant">
      <v-col cols="12" md="5">
        <v-card outlined>
          <v-card-title class="headline text-h6">
            <v-icon left color="primary">mdi-message-plus</v-icon>
            Upravljanje obaveštenjem
          </v-card-title>

          <v-card-text>
            <p class="body-2 text--secondary">
              Kreiranjem nove poruke, sve prethodne poruke će automatski postati neaktivne.
            </p>
            <v-form ref="form" v-model="valid" @submit.prevent="createNewOutage">
              <v-tabs v-model="activeLanguageTab" color="primary" class="mb-4">
                <v-tab v-for="lang in availableLanguages" :key="lang.code">
                  {{ lang.text }}
                </v-tab>
              </v-tabs>

              <v-tabs-items v-model="activeLanguageTab">
                <v-tab-item v-for="lang in availableLanguages" :key="lang.code">
                  <v-text-field
                    v-if="lang.code === 'default'"
                    v-model="newTitle"
                    label="Naslov obaveštenja"
                    :rules="[v => !!v || 'Naslov je obavezan']"
                    outlined
                    required
                  ></v-text-field>
                  <v-text-field
                    v-else
                    v-model="outageTranslations.find((t) => t.code === lang.code).title"
                    label="Naslov obaveštenja (translate)"
                    outlined
                  ></v-text-field>

                  <v-textarea
                    v-if="lang.code === 'default'"
                    v-model="newMessage"
                    label="Tekst obaveštenja"
                    :rules="[v => !!v || 'Poruka je obavezna']"
                    outlined
                    rows="4"
                    required
                  ></v-textarea>
                  <v-textarea
                    v-else
                    v-model="outageTranslations.find((t) => t.code === lang.code).message"
                    label="Tekst obaveštenja (translate)"
                    outlined
                    rows="4"
                  ></v-textarea>
                </v-tab-item>
              </v-tabs-items>

              <v-btn
                type="submit"
                color="primary"
                :disabled="!valid"
                :loading="loading.store"
                block
              >
                Aktiviraj i Sačuvaj
              </v-btn>
            </v-form>
          </v-card-text>
        </v-card>
      </v-col>

      <v-col cols="12" md="7">
        <v-card outlined>
          <v-card-title class="headline text-h6">
            <v-icon left color="primary">mdi-history</v-icon>
            Istorija i aktivacija poruka
          </v-card-title>

          <v-data-table
            :headers="headers"
            :items="outagesHistory"
            :loading="loading.history"
            no-data-text="Nema kreiranih poruka u istoriji"
            loading-text="Učitavanje poruka..."
            hide-default-footer
            class="elevation-0"
          >
            <template v-slot:item.is_active="{ item }">
              <v-chip
                :color="item.is_active ? 'success' : 'grey lighten-1'"
                text-color="white"
                small
              >
                {{ item.is_active ? 'Aktivna' : 'Neaktivna' }}
              </v-chip>
            </template>

            <template v-slot:item.title="{ item }">
              <div>
                <strong>{{ item.title }}</strong>
                <div>{{ item.message }}</div>
              </div>
            </template>

            <template v-slot:item.created_at="{ item }">
              {{ formatDate(item.created_at) }}
            </template>

            <template v-slot:item.actions="{ item }">
              <v-btn
                v-if="item.is_active"
                small
                color="error"
                outlined
                :loading="loading.toggle === item.id"
                @click="deactivateActiveOutage(item.id)"
              >
                Deaktiviraj
              </v-btn>
              <v-btn
                v-else
                small
                color="success"
                outlined
                :disabled="loading.toggle === item.id"
                :loading="loading.toggle === item.id"
                @click="activateOutage(item.id)"
              >
                Aktiviraj
              </v-btn>
            </template>
          </v-data-table>
        </v-card>
      </v-col>
    </v-row>

    <v-snackbar v-model="snackbar.show" :color="snackbar.color" timeout="4000">
      {{ snackbar.text }}
      <template v-slot:action="{ attrs }">
        <v-btn text v-bind="attrs" @click="snackbar.show = false">Zatvori</v-btn>
      </template>
    </v-snackbar>
  </v-container>
</template>

<script>
import axios from '../../plugins/axios';

export default {
  name: 'OutageManager',
  data() {
    return {
      activeOutage: null,
      outagesHistory: [],
      activeLanguageTab: 0,
      newMessage: '',
      newTitle: '',
      outageTranslations: [],
      valid: false,

      loading: {
        active: false,
        history: false,
        store: false,
        deactivate: false, // Loader za glavno dugme za gašenje
        toggle: null
      },

      headers: [
        { text: 'Naslov', value: 'title', sortable: false },
        { text: 'Datum', value: 'created_at', width: '130px' },
        { text: 'Status', value: 'is_active', width: '110px', align: 'center' },
        { text: 'Akcija', value: 'actions', width: '120px', sortable: false, align: 'center' },
      ],

      snackbar: {
        show: false,
        text: '',
        color: 'success'
      }
    };
  },
  computed: {
    availableLanguages() {
      const restaurant =
        this.$store.state.restaurant || this.$store.state.company?.restaurant;
      const restaurantLanguages = restaurant?.settings?.language || [];

      const def = [
        {
          code: 'default',
          text: 'default',
          locale: 'default',
          flag: '🇷',
          script: 'default',
        },
      ];

      if (restaurantLanguages.length === 0) {
        return def.concat(this.getAvailableLanguages());
      }

      return def.concat(
        this.getAvailableLanguages().filter((lang) =>
          restaurantLanguages.includes(lang.code)
        )
      );
    },
    isAdminOrRestaurant() {
      return true; // Prilagodi svom Auth sistemu
    }
  },
  created() {
    this.initializeOutageTranslations();
    this.getActiveOutage();
    if (this.isAdminOrRestaurant) {
      this.getOutagesHistory();
    }
  },
  watch: {
    availableLanguages: {
      handler() {
        this.initializeOutageTranslations();
      },
      deep: true,
    },
  },
  methods: {
    initializeOutageTranslations() {
      this.outageTranslations = [];
      const existingTranslations = this.outageTranslations || [];
      this.outageTranslations = this.availableLanguages.map((lang) => {
        const found = existingTranslations.find((t) => t.code === lang.code);
        return found
          ? { ...found }
          : { code: lang.code, title: '', message: '' };
      });
    },
    getActiveOutage() {
      this.loading.active = true;
      axios.get('/outage/active?lang=' + this.$i18n.locale)
        .then(response => {
          this.activeOutage = response.data;
        })
        .catch(error => {
          if (error.response && error.response.status === 404) {
            this.activeOutage = null; // Znači da nema aktivne poruke
          } else {
            this.showSnackbar('Greška pri učitavanju aktivnog obaveštenja', 'error');
          }
        })
        .finally(() => {
          this.loading.active = false;
        });
    },

    getOutagesHistory() {
      this.loading.history = true;
      axios.get('/outage?lang=' + this.$i18n.locale)
        .then(response => {
          this.outagesHistory = response.data;
        })
        .catch(() => {
          this.showSnackbar('Greška pri učitavanju istorije poruka', 'error');
        })
        .finally(() => {
          this.loading.history = false;
        });
    },

    createNewOutage() {
      if (!this.$refs.form.validate()) return;

      this.loading.store = true;
      const translations = (this.outageTranslations || [])
        .filter((translation) => translation.code !== 'default')
        .filter((translation) => translation.title || translation.message);

      axios.post('/outage', {
        message: this.newMessage,
        title: this.newTitle,
        translations,
      })
        .then(response => {
          this.showSnackbar(response.data.message || 'Poruka uspešno aktivirana!');
          this.newMessage = '';
          this.newTitle = '';
          this.initializeOutageTranslations();
          this.$refs.form.resetValidation();
          this.refreshData();
        })
        .catch(() => {
          this.showSnackbar('Greška prilikom čuvanja poruke', 'error');
        })
        .finally(() => {
          this.loading.store = false;
        });
    },

    activateOutage(id) {
      this.loading.toggle = id;
      axios.patch(`/outage/${id}/activate`)
        .then(response => {
          this.showSnackbar(response.data.message);
          this.refreshData();
        })
        .catch(() => {
          this.showSnackbar('Greška prilikom promene aktivne poruke', 'error');
        })
        .finally(() => {
          this.loading.toggle = null;
        });
    },

    // NOVA METODA: Gasi sve poruke u bazi
    deactivateActiveOutage(itemId = null) {
      // Ako je kliknuto iz tabele, stavljamo loader na taj red, inače na glavno dugme
      if (itemId) {
        this.loading.toggle = itemId;
      } else {
        this.loading.deactivate = true;
      }

      axios.post('/outage/deactivate-all')
        .then(response => {
          this.showSnackbar('Obaveštenje uspešno deaktivirano. Trenutno nema aktivnih poruka.');
          this.refreshData();
        })
        .catch(() => {
          this.showSnackbar('Greška prilikom gašenja obaveštenja', 'error');
        })
        .finally(() => {
          this.loading.deactivate = false;
          this.loading.toggle = null;
        });
    },

    // Pomoćna metoda za osvežavanje oba izvora podataka
    refreshData() {
      this.getActiveOutage();
      this.getOutagesHistory();
    },

    showSnackbar(text, color = 'success') {
      this.snackbar.text = text;
      this.snackbar.color = color;
      this.snackbar.show = true;
    },
    formatDate(dateString) {
      if (!dateString) return '';
      const date = new Date(dateString);
      return date.toLocaleDateString('sr-RS', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    }
  }
};
</script>
